/* eslint-disable no-param-reassign */
import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams, useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { cloneDeep, isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import * as componentAction from '../../store/component.action';

export const StateContext = createContext(null);

export default function POrderDetailStateProvider({ children }) {
  const { id } = useParams();
  const { supplierType } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation('common');
  const searchParams = new URLSearchParams(location?.search);
  const pId = searchParams.get('pId');
  const isDuplicate = searchParams.get('forDuplication');

  const reducerState = useSelector((redState) => redState);
  const {
    ComponentOrderReducer: { pODetailData, poListData, editData, supplierList, userContactData, UnitOfMeasure },
    UserDetails: { userInfo },
  } = reducerState;
  const loggedInUserId = userInfo?.userId;

  const initialRowData = {
    id: 1,
    reference: '',
    description: '',
    colorway: '',
    size: '',
    sizeType: '',
    quantity: '',
    quantityType: '',
    price: '',
    per: '',
    hasError: false,
    currency: '',
  };

  const initialSetting = {
    PDetailData: [],
    poSelectedDate: '',
    poOrderNo: '',
    dropModal: false,
    activetemplate: [],
    templateJsonSchema: [],
    selectedTemplate: null,
    rowsData: [initialRowData],
    supplierID: '',
    createdBy: '',
    poFilesModalState: false,
    confirmModalType: null,
    confirmModalState: false,
    confirmModalMessage: '',
    confirmModalSecondMessage: '',
    confirmModalThirdMessage: '',
    confirmModalShowthirdMsg: false,
    confirmModalTitle: '',
    confirmModalCommentState: false,
    confirmModalCommentMsg: '',
    confirmModalLoading: false,
    handOverDate: '',
    shipmentDate: '',
    statusLog: [],
    showMarkDelievered: false,
    selectedDeliveryDate: '',
    isDelievered: false,
    showDateError: false,
    setDeliveredDate: '',
    openPOFiles: false,
    poFileData: null,
  };

  const [poDetailstate, setPoDetailState] = useState({ ...initialSetting });
  const previousStatusIndex = poDetailstate?.statusLog?.length ? poDetailstate.statusLog.length - 1 : '';
  const previousStatus = poDetailstate?.statusLog?.[previousStatusIndex];
  const disablePoCondition =
    !isDuplicate &&
    (supplierType ||
      pId ||
      poDetailstate?.isDelievered ||
      previousStatus?.status === 'Approved' ||
      previousStatus?.status === 'Cancelled' ||
      previousStatus?.status === 'Canceled');

  useEffect(() => {
    return () => {
      setPoDetailState(initialSetting);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (id) {
      const fetchPurcahseOrder = async () => {
        const reqObj = {
          isActive: true,
        };
        await Promise.all([
          dispatch(componentAction.getPurchaseOrderById(id)),
          dispatch(componentAction.fetchAllSupplier()),
          dispatch(componentAction.fetchContactPersonList()),
          dispatch(
            componentAction.fetchBomLibarayUnitOfMeasure({
              isActive: true,
              loggedInUserId,
            }),
          ),
        ]);
        if (!supplierType) dispatch(componentAction.fetchPOTemplateList(reqObj));
      };
      fetchPurcahseOrder();
    }
  }, [id, dispatch, supplierType, loggedInUserId]);

  useEffect(() => {
    if (pId) {
      dispatch(componentAction.getPublicPurchaseOrderById(pId));
    }
  }, [pId, dispatch]);

  const supplierAddress = useCallback((supplier) => {
    const array = [];
    if (supplier?.streetAddress) {
      array.push(supplier.streetAddress);
    }
    if (supplier?.townCity) {
      array.push(supplier.townCity);
    }
    if (supplier?.zipCode) {
      array.push(supplier.zipCode);
    }
    if (supplier?.buildingNo) {
      array.push(supplier.buildingNo);
    }
    if (supplier?.country) {
      array.push(supplier.country);
    }
    return array;
  }, []);

  const updateCompanyColumn = (column) => {
    const contactList = userContactData.map((person) => ({
      ...person,
      name: `${person.firstName} ${person.lastName}`,
    }));
    switch (column?.id) {
      case '1':
        column.options = contactList?.filter((e) => e?.accountVerified);
        column.value = column.value ? column.value : '';
        break;
      case '3':
        column.value = `${poDetailstate?.createdBy?.firstName}  ${poDetailstate?.createdBy?.lastName}`;
        break;
      case '4':
        column.value = column.value ? column.value : poDetailstate?.createdBy?.email || '';
        break;
      default:
        column.value = column.value ? column.value : '';
        break;
    }
    return column;
  };

  const handleSupplierData = (data) => {
    const currentSupplier = supplierList?.find((val) => val._id === poDetailstate?.supplierID);

    const updateColumn = (column, defaultValue) => {
      switch (column?.id) {
        case '1':
          column.value = column?.value || defaultValue;
          break;
        case '2':
          column.value = column?.value || supplierAddress(currentSupplier?.companies).join('\n');
          break;
        case '3':
          column.value = currentSupplier?.companies?.code || '';
          break;
        case '4':
          column.options = currentSupplier?.supplierUser || [];
          column.value = column?.value || '';
          break;
        case '6':
          column.options = currentSupplier?.factories || [];
          column.value = column?.value || '';
          break;
        default:
          column.value = column?.value || '';
          break;
      }
      return column;
    };

    data?.templateJsonSchema?.forEach((tempJson) => {
      if (tempJson.key === 'Supplier_factory') {
        tempJson.columns = tempJson?.columns?.map((columns) => columns.map((column) => updateColumn(column, currentSupplier?.name)));
      }
      if (tempJson.key === 'Company_Details') {
        tempJson.columns = tempJson?.columns?.map((columns) => columns.map((column) => updateCompanyColumn(column)));
      }
    });

    return data;
  };

  useEffect(() => {
    let rightPrevObje = [];
    const existOrderTable = pODetailData?.templateJsonSchema?.filter((e) => {
      return e.key === 'order_details';
    });
    if (existOrderTable?.length === 0) {
      const insertOrderTable = cloneDeep(pODetailData?.templateJsonSchema);
      insertOrderTable.push({ type: 'container', isStatic: true, key: 'order_details', id: '14', label: 'Order Details' });

      rightPrevObje = insertOrderTable;
    } else {
      rightPrevObje = pODetailData?.templateJsonSchema;
    }
    let tempData = [];
    if (rightPrevObje) {
      tempData = JSON.parse(JSON.stringify(rightPrevObje));
    }

    setPoDetailState((prev) => ({
      ...prev,
      PDetailData: pODetailData,
      poOrderNo: pODetailData.purchaseOrderNo,
      handOverDate: pODetailData?.handOverDate,
      shipmentDate: pODetailData?.shipmentDate,
      activetemplate: poListData,
      supplierID: pODetailData?.supplierId,
      createdBy: pODetailData?.createdBy,
      templateJsonSchema: tempData,
      statusLog: pODetailData?.statusLog,
      poSelectedDate: pODetailData?.poSelectedDate,
      setDeliveredDate: pODetailData?.deliveryDate,
      isDelievered: pODetailData?.isDelievered,
      rowsData: pODetailData?.requirements?.length
        ? pODetailData?.requirements.map((item) => ({
            ...item,
            hasError: Object.prototype.hasOwnProperty.call(item, 'hasError') ? item.hasError : false,
          }))
        : [initialRowData],
    }));
    handleSupplierData(pODetailData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pODetailData, poListData, supplierList, userContactData]);

  useEffect(() => {
    if (editData && !isEmpty(editData)) {
      setPoDetailState((prev) => ({
        ...prev,
        PDetailData: editData,
        templateJsonSchema: editData?.templateJsonSchema,
      }));
      handleSupplierData(editData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData]);

  const forDropDown = (columnElement) => {
    return columnElement?.key === 'Dropdown'
      ? {
          status: true,
          errorMsg: 'Please select an option',
        }
      : {
          status: true,
          errorMsg: 'Input value is required',
        };
  };
  const timePicker = (columnElement) => {
    return columnElement?.key === 'Time_Picker'
      ? {
          status: true,
          errorMsg: 'Time Selection is required',
        }
      : forDropDown(columnElement);
  };
  const otherOptions = (columnElement) => {
    return columnElement?.key === 'Date_Picker'
      ? {
          status: true,
          errorMsg: 'Date Selection is required',
        }
      : timePicker(columnElement);
  };

  const conditonsForTermAndConditions = (element) => {
    element.columns.forEach((column) => {
      column.forEach((columnElement) => {
        if (columnElement?.required && columnElement?.enable) {
          if (
            columnElement?.key === 'Text_Area' ||
            columnElement?.key === 'Text' ||
            columnElement?.key === 'Ratio_Button' ||
            columnElement?.key === 'Date_Picker' ||
            columnElement?.key === 'Dropdown' ||
            columnElement?.key === 'Time_Picker'
          ) {
            if (!columnElement?.value) {
              columnElement.hasError =
                columnElement?.key === 'Ratio_Button'
                  ? {
                      status: true,
                      errorMsg: 'selection is required',
                    }
                  : otherOptions(columnElement);
            }
          } else {
            let hasSelectedCheckbox = false;
            columnElement.options.forEach((optionElement) => {
              if (optionElement.value) {
                hasSelectedCheckbox = true;
              }
            });
            if (!hasSelectedCheckbox) {
              columnElement.hasError = {
                status: true,
                errorMsg: 'Please select at least one Checkbox',
              };
            } else {
              columnElement.hasError = {
                status: false,
                errorMsg: '',
              };
            }
          }
        }
      });
    });
  };

  const redirectToListingPage = useCallback(() => {
    setPoDetailState((prev) => ({
      ...prev,
      templateJsonSchema: [],
    }));
    dispatch(componentAction.clearReducer());
    if (supplierType) {
      history.push({
        pathname: `/sub-supplier/purchase-order/${supplierType}`,
      });
    } else {
      history.push({
        pathname: '/component-order/purchase-order',
      });
    }
  }, [setPoDetailState, dispatch, history, supplierType]);

  const handleTempletJsonRequiredField = useCallback((name, element) => {
    if (name === 'Shipping_Details' || name === 'Supplier_factory' || name === 'Company_Details' || name === 'Section') {
      conditonsForTermAndConditions(element);
    }
  }, []);

  const checkValidation = useCallback(() => {
    poDetailstate?.templateJsonSchema?.forEach((element) => {
      handleTempletJsonRequiredField(element?.key, element);
    });
    setPoDetailState((prevState) => ({
      ...prevState,
      ...poDetailstate,
    }));
  }, [poDetailstate, handleTempletJsonRequiredField, setPoDetailState]);

  const orderValidation = useCallback(async () => {
    const emptyFields = [];
    poDetailstate?.rowsData.forEach((item, index) => {
      Object.keys(item).forEach((key) => {
        if (key !== 'hasError' && key !== 'description' && key !== 'colorway' && key !== 'size' && key !== 'sizeType' && !item[key]) {
          item.hasError = true;
          emptyFields.push({ index, key });
        }
      });
    });

    const updateData = poDetailstate?.rowsData?.map((item, index) => ({
      ...item,
      hasError: emptyFields.some((field) => field.index === index),
    }));

    setPoDetailState((prevState) => ({
      ...prevState,
      rowsData: updateData,
    }));
  }, [poDetailstate, setPoDetailState]);

  const savePurchaseOrderData = useCallback(async () => {
    await orderValidation();
    checkValidation();
    const checlAllFields = poDetailstate?.templateJsonSchema.map((data) => {
      const coloum = data?.columns;
      let checkVal = false;
      if (coloum) {
        const newdata = coloum?.map((res) => {
          const errData = res.find((ele) => ele?.hasError?.status);
          return !!errData;
        });
        checkVal = !!newdata.includes(true);
      }
      return checkVal;
    });
    const checkHasError = poDetailstate?.rowsData.find((ele) => ele.hasError);
    if (!checkHasError && !checlAllFields?.includes(true)) {
      let res;
      const reqObj = {
        ...pODetailData,
        templateJsonSchema: JSON.stringify(poDetailstate?.templateJsonSchema),
        requirements: JSON.stringify(poDetailstate?.rowsData),
        poSelectedDate: poDetailstate?.poSelectedDate,
        handOverDate: poDetailstate?.handOverDate,
        shipmentDate: poDetailstate?.shipmentDate,
        purchaseOrderNo: poDetailstate?.poOrderNo,
        poTemplateId: poDetailstate?.selectedTemplate?._id ? poDetailstate?.selectedTemplate?._id : poDetailstate?.PDetailData?.poTemplateId,
        name: poDetailstate?.PDetailData?.name,
        image: poDetailstate?.PDetailData?.image,
      };

      if (poDetailstate?.PDetailData?.imageKey && reqObj?.image?.startsWith('https://')) {
        reqObj.image = poDetailstate?.PDetailData?.imageKey;
      }

      if (isDuplicate) {
        reqObj.purchaseOrderId = poDetailstate?.poOrderNo;
        delete reqObj?.deliveryDate;
        delete reqObj?.isDelievered;
        delete reqObj?._id;
        res = await dispatch(componentAction.saveDuplicatePurachseOrder(reqObj, t));
      } else {
        res = await dispatch(componentAction.saveEditPurchaseOrder(id, reqObj, t));
      }
      if (res) {
        redirectToListingPage();
      }
    }
  }, [orderValidation, checkValidation, poDetailstate, pODetailData, isDuplicate, id, dispatch, t, redirectToListingPage]);

  const memoizedContextValue = useMemo(() => {
    return {
      poDetailstate,
      setPoDetailState,
      supplierAddress,
      savePurchaseOrderData,
      redirectToListingPage,
      supplierType,
      pId,
      UnitOfMeasure,
      pODetailData,
      disablePoCondition,
      userInfo,
    };
  }, [
    poDetailstate,
    setPoDetailState,
    supplierAddress,
    savePurchaseOrderData,
    redirectToListingPage,
    supplierType,
    pId,
    UnitOfMeasure,
    pODetailData,
    disablePoCondition,
    userInfo,
  ]);

  return <StateContext.Provider value={memoizedContextValue}>{children}</StateContext.Provider>;
}

export function usePOrderDetailState() {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useAppState must be used within the ProductCostingStateProvider');
  }
  return context;
}
